/**
 * Decision Brief — Record the Decision
 *
 * The decision authority receives the submitted proposals (the upward handoff),
 * reviews them against the locked Frame, and issues a formal resolution:
 *
 *   Accept                  — one or more options approved, decision on record
 *   Accept with Conditions  — approved, subject to named conditions
 *   Defer                   — decision delayed; named signal that would unblock it
 *   Reject all              — none approved; all rejected with rationale
 *
 * The resulting record answers four questions:
 *   1. What was chosen, and why?
 *   2. What was rejected, and why?
 *   3. Under what signal does this decision become eligible for revisitation?
 *   4. Who decided, and when?
 *
 * This is not a summary. It is the Plan of Record.
 */

import { useState, useEffect } from 'react';
import {
  CheckIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  XMarkIcon,
  CheckCircleIcon,
  // ExclamationTriangleIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { recordBriefDecision, reopenBrief } from '../../services/brief/briefService';
import { exportToPrioritizationPlatform } from '../../services/brief/briefExportService';
import { promoteBriefToRecord } from '../../services/brief/briefPromotionService';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import type {
  Brief,
  BriefOptionWithProposal,
  BriefDecision,
  BriefResolutionType,
  PromotionType,
} from '../../types/brief';
import {
  BRIEF_RESOLUTION_LABELS,
  PROMOTION_TYPE_LABELS,
  PROMOTION_TYPE_DESCRIPTIONS,
} from '../../types/brief';
import type { DecisionImpact } from '../../types/prioritization/decisions';

const inputCls =
  'w-full border border-[var(--rule-bright)]  px-3 py-2 text-sm text-[var(--text-pri)] bg-[var(--surface)] placeholder:text-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-[var(--slate)] focus:border-transparent transition-shadow disabled:opacity-60 disabled:cursor-default';

const labelCls = 'block text-sm font-medium text-[var(--text-sec)] mb-1';
const hintCls = 'text-xs text-[var(--text-ter)] mt-1';

// Resolution types — mirrors Catchball: Accept, Accept with Conditions, Defer, Reject.
// Stored as brief_decisions.resolution_type since migration 910.
type ResolutionType = BriefResolutionType;

const RESOLUTION_CONFIG: Record<ResolutionType, {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  selectedColor: string;
}> = {
  accept: {
    label: 'Accept',
    description: 'One or more options are approved. Decision is on record.',
    icon: <CheckCircleIcon className="w-4 h-4" />,
    color: 'border-[var(--rule)] text-[var(--text-ter)]',
    selectedColor: 'border-[var(--green)]/50 bg-[var(--green)]/10 text-[var(--green)] dark:text-[var(--text-ter)]',
  },
  accept_with_conditions: {
    label: 'Accept with Conditions',
    description: 'Approved, subject to named conditions that must be satisfied.',
    icon: <CheckIcon className="w-4 h-4" />,
    color: 'border-[var(--rule)] text-[var(--text-ter)]',
    selectedColor: 'border-[var(--mustard)]/50 bg-[var(--mustard)]/10 text-[var(--mustard)]',
  },
  defer: {
    label: 'Defer',
    description: 'Not decided yet. Say when to look at it again.',
    icon: <ClockIcon className="w-4 h-4" />,
    color: 'border-[var(--rule)] text-[var(--text-ter)]',
    selectedColor: 'border-[var(--slate)]/50 bg-[var(--slate)]/10 text-[var(--text-ter)]',
  },
  reject_all: {
    label: 'Reject all options',
    description: 'None approved. All options rejected with documented rationale.',
    icon: <XMarkIcon className="w-4 h-4" />,
    color: 'border-[var(--rule)] text-[var(--text-ter)]',
    selectedColor: 'border-[var(--coral)]/50 bg-[var(--coral)]/10 text-[var(--coral)]',
  },
};

interface DecisionPanelProps {
  brief: Brief;
  options: BriefOptionWithProposal[];
  decision: BriefDecision | null;
  /** Only the person who set up the decision records it or reopens it (910). */
  isCreator: boolean;
  onDecisionSaved: (d: BriefDecision) => void;
  onReopened: (b: Brief) => void;
  onNavigateToView: () => void;
}

export default function DecisionPanel({
  brief,
  options,
  decision,
  isCreator,
  onDecisionSaved,
  onReopened,
  onNavigateToView,
}: DecisionPanelProps) {
  const { userId: currentUserId } = useUserStore();
  const { currentWorkspaceId } = useTenantStore();
  const [exportingToPlatform, setExportingToPlatform] = useState(false);
  const [exportResult, setExportResult] = useState<{ itemsCreated: number } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Plan of Record promotion state
  const [promotionType, setPromotionType] = useState<PromotionType>('creates');
  const [promotionNote, setPromotionNote] = useState('');
  const [promotionImpact, setPromotionImpact] = useState<DecisionImpact>('high');
  const [promoting, setPromoting] = useState(false);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [promotedDecisionId, setPromotedDecisionId] = useState<string | null>(
    brief.promoted_decision_id ?? null
  );

  const [resolutionType, setResolutionType] = useState<ResolutionType>('accept');
  const [selectedIds, setSelectedIds] = useState<string[]>(decision?.selected_option_ids ?? []);
  const [selectedRationale, setSelectedRationale] = useState(decision?.selected_rationale ?? '');
  const [rejectedRationale, setRejectedRationale] = useState(decision?.rejected_rationale ?? '');
  const [conditions, setConditions] = useState(decision?.conditions ?? '');
  const [signalForChange, setSignalForChange] = useState(decision?.signal_for_change ?? '');
  const [revisitWhen, setRevisitWhen] = useState(decision?.revisit_when ?? '');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLocked = brief.status === 'decided' || brief.status === 'deferred' || brief.status === 'archived';
  const canReopen = isCreator && (brief.status === 'decided' || brief.status === 'deferred') && !promotedDecisionId;

  useEffect(() => {
    if (decision) {
      setSelectedIds(decision.selected_option_ids ?? []);
      setSelectedRationale(decision.selected_rationale ?? '');
      setRejectedRationale(decision.rejected_rationale ?? '');
      setConditions(decision.conditions ?? '');
      setSignalForChange(decision.signal_for_change ?? '');
      setRevisitWhen(decision.revisit_when ?? '');
    }
  }, [decision]);

  const toggleOption = (id: string) => {
    if (isLocked) return;
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const needsSelections = resolutionType === 'accept' || resolutionType === 'accept_with_conditions';

  const canSave = () => {
    if (isLocked) return false;
    if (!currentUserId) return false;
    if (needsSelections && selectedIds.length === 0) return false;
    if (!selectedRationale.trim()) return false;
    if (resolutionType === 'accept_with_conditions' && !conditions.trim()) return false;
    if (resolutionType === 'defer' && !revisitWhen.trim()) return false;
    return true;
  };

  const handleSave = async () => {
    if (!canSave()) return;
    setSaving(true);
    setError(null);

    try {
      const result = await recordBriefDecision(brief.id, {
        resolution_type: resolutionType,
        selected_option_ids: needsSelections ? selectedIds : [],
        selected_rationale: selectedRationale,
        rejected_rationale: needsSelections ? rejectedRationale : null,
        conditions: resolutionType === 'accept_with_conditions' ? conditions : null,
        signal_for_change: resolutionType === 'defer' ? null : signalForChange,
        revisit_when: resolutionType === 'defer' ? revisitWhen : null,
      });
      onDecisionSaved(result);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record decision');
    } finally {
      setSaving(false);
    }
  };

  const rejectedOptions = options.filter((o) => !selectedIds.includes(o.id));

  const handleReopen = async () => {
    if (!reopenReason.trim()) return;
    setReopening(true);
    setError(null);
    try {
      const reopened = await reopenBrief(brief.id, reopenReason.trim());
      setReopenOpen(false);
      setReopenReason('');
      setResolutionType('accept');
      onReopened(reopened);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reopen');
    } finally {
      setReopening(false);
    }
  };

  const lockedResolution: ResolutionType | null =
    decision?.resolution_type ?? (brief.status === 'deferred' ? 'defer' : null);

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Decision on record banner */}
      {brief.status === 'decided' && (
        <div className="border border-[var(--green)]/30 bg-[var(--green)]/10 px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-[var(--green)]" />
            <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] font-medium">Decision on record</p>
          </div>
          <button
            onClick={onNavigateToView}
            className="inline-flex items-center gap-1.5 text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] underline transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            View Decision Record
          </button>
        </div>
      )}

      {brief.status === 'deferred' && (
        <div className="border border-[var(--slate)]/30 bg-[var(--slate)]/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-4 h-4 text-[var(--text-ter)]" />
            <p className="text-sm text-[var(--text-pri)] font-medium">
              Deferred{decision?.revisit_when ? ` until ${decision.revisit_when}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* Only the person who set this up records the decision; say so instead of a button that would be refused. */}
      {!isLocked && !isCreator && (
        <p className="text-sm text-[var(--text-ter)]">
          The person who set up this decision records it{brief.decision_authority ? ` with ${brief.decision_authority}` : ''}.
        </p>
      )}

      {/* Resolution type — mirrors Catchball resolution options */}
      {!isLocked && isCreator && (
        <div>
          <label className={labelCls}>Resolution type <span className="text-[var(--coral)]">*</span></label>
          <p className={hintCls + ' mb-3'}>
            The form of this decision. Mirrors the four resolution types in a governed authority exchange.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(Object.entries(RESOLUTION_CONFIG) as [ResolutionType, (typeof RESOLUTION_CONFIG)[ResolutionType]][]).map(([type, cfg]) => (
              <button
                key={type}
                onClick={() => setResolutionType(type)}
                className={`text-left border  px-3 py-3 transition-colors ${
                  resolutionType === type ? cfg.selectedColor : `${cfg.color} hover:border-[var(--rule-bright)]`
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {cfg.icon}
                  <span className="text-sm font-medium">{cfg.label}</span>
                </div>
                <p className="text-xs opacity-70">{cfg.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {isLocked && decision && (
        <div>
          <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-2">Resolution</p>
          <p className="text-sm text-[var(--text-sec)]">
            {lockedResolution
              ? BRIEF_RESOLUTION_LABELS[lockedResolution]
              : (decision.selected_option_ids?.length ?? 0) > 0 ? 'Accepted' : 'Rejected'}
          </p>
          {decision.selected_rationale && (
            <p className="text-sm text-[var(--text-pri)] mt-2 whitespace-pre-wrap">{decision.selected_rationale}</p>
          )}
          {decision.conditions && (
            <p className="text-sm text-[var(--text-sec)] mt-2 whitespace-pre-wrap">Conditions: {decision.conditions}</p>
          )}
          {decision.signal_for_change && (
            <p className="text-sm text-[var(--text-sec)] mt-2 whitespace-pre-wrap">What would make us look at this again: {decision.signal_for_change}</p>
          )}
        </div>
      )}

      {/* Reopen — returns a decided or deferred brief to comparing, with a reason (910) */}
      {canReopen && (
        <div>
          {!reopenOpen ? (
            <button
              onClick={() => setReopenOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule-bright)] text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Reopen
            </button>
          ) : (
            <div className="border border-[var(--rule)] p-4 space-y-3 bg-[var(--surface)]">
              <label className={labelCls} htmlFor="brief-reopen-reason">Why reopen it? <span className="text-[var(--coral)]">*</span></label>
              <textarea
                id="brief-reopen-reason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                rows={2}
                placeholder="What changed? The reason and the decision being set aside are kept in the history."
                className={inputCls}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReopen}
                  disabled={reopening || !reopenReason.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--text-pri)] text-[var(--bg)] text-sm font-medium hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                >
                  {reopening ? 'Reopening…' : 'Reopen and compare again'}
                </button>
                <button
                  onClick={() => { setReopenOpen(false); setReopenReason(''); }}
                  className="px-3 py-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {isCreator && (brief.status === 'decided' || brief.status === 'deferred') && promotedDecisionId && (
        <p className="text-xs text-[var(--text-ter)]">
          This decision is on the plan, so it is not reopened here. To change it, start a new decision that changes or reverses it.
        </p>
      )}

      {/* Option selection — for Accept types */}
      {((needsSelections && isCreator) || (isLocked && lockedResolution !== 'defer')) && (
        <div>
          <label className={labelCls}>
            What was selected? {needsSelections && !isLocked && <span className="text-[var(--coral)]">*</span>}
          </label>
          <p className={hintCls + ' mb-3'}>Select one or more options that were approved.</p>
          <div className="space-y-2">
            {options.map((o) => {
              const isSelected = selectedIds.includes(o.id);
              return (
                <button
                  key={o.id}
                  disabled={isLocked || !needsSelections}
                  onClick={() => toggleOption(o.id)}
                  className={`w-full text-left border  px-4 py-3 transition-colors ${
                    isSelected
                      ? 'border-[var(--green)]/40 bg-[var(--green)]/10'
                      : 'border-[var(--rule)] bg-[var(--surface)]'
                  } ${(isLocked || !needsSelections) ? 'cursor-default' : 'hover:border-[var(--rule-bright)]'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected ? 'border-[var(--green)] bg-[var(--green)]' : 'border-[var(--text-dim)]'
                    }`}>
                      {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-pri)]">{o.name}</p>
                      {o.owner_name && <p className="text-xs text-[var(--text-ter)]">Owner: {o.owner_name}</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Defer: reason for delay, and when to look again */}
      {resolutionType === 'defer' && !isLocked && isCreator && (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Why is the decision being deferred? <span className="text-[var(--coral)]">*</span></label>
            <textarea
              value={selectedRationale}
              onChange={(e) => setSelectedRationale(e.target.value)}
              rows={3}
              placeholder="What information or event must occur before this decision can be made? Name the gap specifically."
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>When should we look at it again? <span className="text-[var(--coral)]">*</span></label>
            <input
              type="text"
              value={revisitWhen}
              onChange={(e) => setRevisitWhen(e.target.value)}
              placeholder='e.g. "After the Q1 budget is set" or "15 January"'
              className={inputCls}
            />
          </div>
        </div>
      )}

      {/* Accept / Accept with conditions: selected rationale */}
      {needsSelections && !isLocked && isCreator && (
        <div>
          <label className={labelCls}>Why was this chosen? <span className="text-[var(--coral)]">*</span></label>
          <textarea
            value={selectedRationale}
            onChange={(e) => setSelectedRationale(e.target.value)}
            disabled={isLocked}
            rows={3}
            placeholder={
              selectedIds.length === 0
                ? 'Select one or more options above first'
                : `Summarize the rationale for choosing ${options.find((o) => o.id === selectedIds[0])?.name ?? 'this option'}. Write this for someone reading it six months from now.`
            }
            className={inputCls}
          />
          <p className={hintCls}>
            Write this for someone reading it six months from now. The rationale is the record — not the spreadsheet, not the meeting.
          </p>
        </div>
      )}

      {/* Reject all: rationale for rejection */}
      {resolutionType === 'reject_all' && !isLocked && isCreator && (
        <div>
          <label className={labelCls}>Why were all options rejected? <span className="text-[var(--coral)]">*</span></label>
          <textarea
            value={selectedRationale}
            onChange={(e) => setSelectedRationale(e.target.value)}
            rows={3}
            placeholder="What was insufficient in every option? What would a viable option need to look like?"
            className={inputCls}
          />
        </div>
      )}

      {/* Rejected rationale — for when some options lost */}
      {rejectedOptions.length > 0 && needsSelections && !isLocked && isCreator && (
        <div>
          <label className={labelCls}>Why were the others not chosen?</label>
          <textarea
            value={rejectedRationale}
            onChange={(e) => setRejectedRationale(e.target.value)}
            disabled={isLocked}
            rows={3}
            placeholder={`Document what was insufficient about ${rejectedOptions.map((o) => o.name).join(', ')}. This is the field that prevents relitigating the same options.`}
            className={inputCls}
          />
          <p className={hintCls}>
            This is the most valuable field. Documenting why options lost is what prevents this decision from being relitigated.
          </p>
        </div>
      )}

      {/* Accept with Conditions: named conditions */}
      {resolutionType === 'accept_with_conditions' && !isLocked && isCreator && (
        <div>
          <label className={labelCls}>Conditions <span className="text-[var(--coral)]">*</span></label>
          <textarea
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            rows={2}
            placeholder='e.g. "Contingent on confirming the API partnership before March 1" or "Headcount must reach 6 engineers before kicking off."'
            className={inputCls}
          />
          <p className={hintCls}>Named conditions make acceptance durable rather than tentative. Be specific.</p>
        </div>
      )}

      {/* Signal for change — the instrument panel concept from F1 */}
      {!isLocked && isCreator && resolutionType !== 'defer' && (
        <div>
          <label className={labelCls}>What signal would make this decision eligible for change?</label>
          <textarea
            value={signalForChange}
            onChange={(e) => setSignalForChange(e.target.value)}
            rows={2}
            placeholder='e.g. "If the partner API is deprecated, or if team capacity drops below 4 engineers for two consecutive quarters."&#10;&#10;Without a named signal, all change looks the same — a considered response to new information is indistinguishable from an unanchored course change.'
            className={inputCls}
          />
          <p className={hintCls}>
            This closes the loop. A decision without a named signal for change cannot be evaluated when change is proposed.
            Change that cannot be evaluated looks identical to drift.
          </p>
        </div>
      )}

      {error && (
        <div className="border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-3 py-2 text-sm text-[var(--coral)]">{error}</div>
      )}

      {/* Save */}
      {!isLocked && isCreator && (
        <div className="pt-2 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving || !canSave()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--text-pri)] text-[var(--bg)] text-sm font-medium hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            {saving ? 'Recording...' : (
              <>
                <CheckIcon className="w-4 h-4" />
                {resolutionType === 'accept' && 'Record decision'}
                {resolutionType === 'accept_with_conditions' && 'Accept with conditions'}
                {resolutionType === 'defer' && 'Defer decision'}
                {resolutionType === 'reject_all' && 'Reject all options'}
              </>
            )}
          </button>
          {saved && (
            <span className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1.5">
              <CheckIcon className="w-4 h-4" /> Decision on record
            </span>
          )}
        </div>
      )}

      {/* Decided: view link */}
      {brief.status === 'decided' && (
        <div className="flex items-center gap-3">
          <button
            onClick={onNavigateToView}
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--rule-bright)] text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:border-[var(--text-ter)] transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            View Decision Record
          </button>
          <p className="text-xs text-[var(--text-ter)]">Share with stakeholders — no account required.</p>
        </div>
      )}

      {/* ── Plan of Record ────────────────────────────────────────────────── */}
      {brief.status === 'decided' && decision && (
        <div className="border-t border-[var(--rule)] pt-6">
          <div className="flex items-center gap-2 mb-1">
            <DocumentTextIcon className="w-4 h-4 text-[var(--text-ter)]" />
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">Put it on the plan</h3>
          </div>
          <p className="text-xs text-[var(--text-ter)] mb-4">
            This decision becomes an entry in the organizational Plan of Record — tracked in the main app
            with full rationale, health monitoring, and signal-for-change detection.
          </p>

          {promotedDecisionId ? (
            /* ── Already promoted ── */
            <div className="border border-[var(--green)]/30 bg-[var(--green)]/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-[var(--green)]" />
                <span className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">On the Plan of Record</span>
                {brief.promotion_type && (
                  <span className="text-xs text-[var(--text-dim)] ml-auto">
                    {PROMOTION_TYPE_LABELS[brief.promotion_type].split(' ').slice(0, 2).join(' ')}
                  </span>
                )}
              </div>
              {brief.promotion_note && (
                <p className="text-xs text-[var(--text-ter)]">Affects: {brief.promotion_note}</p>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <a
                  href={`/prioritization/decisions`}
                  className="inline-flex items-center gap-1.5 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)] underline transition-colors"
                >
                  <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                  View in Plan of Record
                </a>

                {/* Still offer platform export for work item tracking */}
                {!exportResult && currentWorkspaceId && (
                  <button
                    onClick={async () => {
                      if (!currentWorkspaceId) return;
                      setExportingToPlatform(true);
                      setExportError(null);
                      try {
                        const result = await exportToPrioritizationPlatform(options, decision, brief.title, currentWorkspaceId);
                        setExportResult(result);
                      } catch (err) {
                        setExportError(err instanceof Error ? err.message : 'Export failed');
                      } finally {
                        setExportingToPlatform(false);
                      }
                    }}
                    disabled={exportingToPlatform}
                    className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-3 h-3" />
                    {exportingToPlatform ? 'Exporting…' : 'Also export work items'}
                  </button>
                )}
                {exportResult && (
                  <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1">
                    <CheckIcon className="w-3 h-3" />
                    {exportResult.itemsCreated} work item{exportResult.itemsCreated !== 1 ? 's' : ''} created
                  </span>
                )}
              </div>
              {exportError && <p className="text-xs text-[var(--coral)]">{exportError}</p>}
            </div>
          ) : (
            /* ── Promotion form ── */
            <div className="border border-[var(--rule)] p-4 bg-[var(--surface)] space-y-4">

              {/* Promotion type */}
              <div>
                <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-2">
                  What does this decision do to the Plan of Record?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    ['creates',  <PlusCircleIcon className="w-4 h-4" />, 'border-[var(--green)]/50 bg-[var(--green)]/10 text-[var(--green)] dark:text-[var(--text-ter)]'],
                    ['adds',     <DocumentTextIcon className="w-4 h-4" />, 'border-[var(--slate)]/50 bg-[var(--slate)]/10 text-[var(--text-ter)]'],
                    ['changes',  <ArrowPathIcon className="w-4 h-4" />, 'border-[var(--mustard)]/50 bg-[var(--mustard)]/10 text-[var(--mustard)]'],
                    ['reverses', <NoSymbolIcon className="w-4 h-4" />, 'border-[var(--coral)]/50 bg-[var(--coral)]/10 text-[var(--coral)]'],
                  ] as [PromotionType, React.ReactNode, string][]).map(([type, icon, selectedCls]) => (
                    <button
                      key={type}
                      onClick={() => setPromotionType(type)}
                      className={`text-left border  px-3 py-2.5 transition-colors ${
                        promotionType === type
                          ? selectedCls
                          : 'border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule-bright)] hover:text-[var(--text-sec)]'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">{icon}<span className="text-xs font-medium">{PROMOTION_TYPE_LABELS[type].split(' ')[0]}</span></div>
                      <p className="text-[10px] opacity-70 leading-tight">{PROMOTION_TYPE_DESCRIPTIONS[type]}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Note field — required for changes/reverses, optional otherwise */}
              {(promotionType === 'changes' || promotionType === 'reverses' || promotionType === 'adds') && (
                <div>
                  <label className={labelCls}>
                    {promotionType === 'changes' ? 'What prior commitment does this change?' :
                     promotionType === 'reverses' ? 'What commitment is being reversed?' :
                     'What existing plan entry does this add to?'}
                    {(promotionType === 'changes' || promotionType === 'reverses') && (
                      <span className="text-[var(--coral)] ml-1">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={promotionNote}
                    onChange={(e) => setPromotionNote(e.target.value)}
                    placeholder="e.g. Q3 Platform Investment decision, March 2025"
                    className={inputCls}
                  />
                  <p className={hintCls}>
                    Name the prior commitment so the audit trail is unambiguous.
                    The affected decision will be marked superseded in the Plan of Record.
                  </p>
                </div>
              )}

              {/* Impact */}
              <div>
                <label className={labelCls}>Impact</label>
                <div className="flex gap-2">
                  {(['high', 'medium', 'low'] as DecisionImpact[]).map((imp) => (
                    <button
                      key={imp}
                      onClick={() => setPromotionImpact(imp)}
                      className={`text-xs px-3 py-1.5  border capitalize transition-colors ${
                        promotionImpact === imp
                          ? imp === 'high'   ? 'border-[var(--coral)]/40 bg-[var(--coral)]/10 text-[var(--coral)]'
                          : imp === 'medium' ? 'border-[var(--mustard)]/40 bg-[var(--mustard)]/10 text-[var(--mustard)]'
                          :                   'border-[var(--rule-bright)] bg-[var(--surface)] text-[var(--text-sec)]'
                          : 'border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule-bright)]'
                      }`}
                    >
                      {imp}
                    </button>
                  ))}
                </div>
              </div>

              {promotionError && (
                <div className="border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-3 py-2 text-xs text-[var(--coral)]">
                  {promotionError}
                </div>
              )}

              {/* CTA */}
              <button
                disabled={promoting || ((promotionType === 'changes' || promotionType === 'reverses') && !promotionNote.trim())}
                onClick={async () => {
                  if (!brief || !decision) return;
                  setPromoting(true);
                  setPromotionError(null);
                  try {
                    const selectedOptions = options.filter((o) =>
                      (decision.selected_option_ids ?? []).includes(o.id)
                    );
                    const rejectedOptions = options.filter((o) =>
                      !(decision.selected_option_ids ?? []).includes(o.id)
                    );
                    const result = await promoteBriefToRecord({
                      brief,
                      briefDecision: decision,
                      selectedOptions,
                      rejectedOptions,
                      promotionType,
                      promotionNote,
                      impact: promotionImpact,
                    });
                    setPromotedDecisionId(result.decisionId);
                  } catch (err) {
                    setPromotionError(err instanceof Error ? err.message : 'Promotion failed');
                  } finally {
                    setPromoting(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--text-pri)] text-[var(--bg)] text-sm font-medium hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
              >
                <DocumentTextIcon className="w-4 h-4" />
                {promoting ? 'Adding to Plan of Record…' : 'Add to Plan of Record'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
